import { ExternalLink } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { BriefRow } from '../Visit';
import styles from './ChartPrepSections.module.scss';
import { formatVisitDate, getRecordsSearchedLine, showsGroup, showsLabs } from './helpers';

const FLAG = {
  high: { letter: 'H', said: 'high', word: 'High' },
  low: { letter: 'L', said: 'low', word: 'Low' },
  abnormal: { letter: 'A', said: 'abnormal', word: 'Abnormal' },
};

const AthenaLink = ({ href }) =>
  href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={styles.link}>
      Open in Athena
      <ExternalLink size={12} aria-hidden="true" />
    </a>
  ) : null;

AthenaLink.propTypes = { href: PropTypes.string };

const LabValue = ({ cell }) => {
  if (!cell) return <span className={styles.srOnly}>Not in this draw</span>;
  const flag = cell.flag ? FLAG[cell.flag] : null;
  return (
    <span className={flag ? styles.flagged : undefined}>
      {cell.value}
      {flag && (
        <>
          <span aria-hidden="true"> {flag.letter}</span>
          <span className={styles.srOnly}>, {flag.said}</span>
        </>
      )}
    </span>
  );
};

LabValue.propTypes = {
  cell: PropTypes.shape({ value: PropTypes.string, flag: PropTypes.string }),
};

const Disclosure = ({ count, noun, children }) => {
  const [open, setOpen] = useState(false);
  if (!count) return null;
  return (
    <>
      <button
        type="button"
        className={styles.more}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? `Hide the other ${noun}s` : `View ${count} other ${noun}${count === 1 ? '' : 's'}`}
      </button>
      {open && children}
    </>
  );
};

Disclosure.propTypes = {
  count: PropTypes.number,
  noun: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const MAX_DRAWS = 4;

const LabsFlowsheet = ({ labs, chartUrl, todayKey }) => {
  const [showAll, setShowAll] = useState(false);
  const hidden = Math.max(labs.columns.length - MAX_DRAWS, 0);
  const shown = showAll ? labs.columns.length : labs.columns.length - hidden;
  const columns = labs.columns.slice(0, shown);

  return (
    <>
      <div className={styles.scroll}>
        <table className={styles.sheet}>
          <caption className={styles.srOnly}>
            Labs on file, one column per draw, newest first
          </caption>
          <thead>
            <tr>
              <td className={`${styles.testCell} ${styles.corner}`}>
                <AthenaLink href={chartUrl} />
              </td>
              {columns.map((column) => (
                <th key={column.key} scope="col" className={styles.draw}>
                  <span className={styles.drawDate}>{formatVisitDate(column.date, todayKey)}</span>
                  <span className={styles.quiet}>{column.where}</span>
                </th>
              ))}
            </tr>
          </thead>
          {labs.sections.map((section) => (
            <tbody key={section.group}>
              <tr>
                <th scope="colgroup" colSpan={columns.length + 1} className={styles.group}>
                  {section.label}
                </th>
              </tr>
              {section.rows.map((row) => (
                <tr key={row.key} className={styles.row}>
                  <th scope="row" className={`${styles.testCell} ${styles.testName}`}>
                    <span className={styles.testLabel}>{row.label}</span>
                    {(row.unit || row.referenceRange) && (
                      <span className={`${styles.quiet} ${styles.caption}`}>
                        {[row.unit, row.referenceRange].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </th>
                  {row.cells.slice(0, shown).map((cell, index) => (
                    <td key={columns[index].key} className={styles.value}>
                      <LabValue cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
      {hidden > 0 && (
        <button type="button" className={styles.more} onClick={() => setShowAll((value) => !value)}>
          {showAll ? 'Show fewer draws' : `${hidden} earlier ${hidden === 1 ? 'draw' : 'draws'}`}
        </button>
      )}
    </>
  );
};

LabsFlowsheet.propTypes = {
  labs: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    sections: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  chartUrl: PropTypes.string,
  todayKey: PropTypes.string.isRequired,
};

const OtherLabs = ({ labs, todayKey }) => (
  <ul className={styles.lines}>
    {labs.map((lab) => {
      const flag = lab.flag ? FLAG[lab.flag] : null;
      return (
        <li key={lab.key} className={styles.line}>
          <span className={styles.lineLabel}>{lab.label}</span>
          <span className={styles.lineDate}>{formatVisitDate(lab.date, todayKey)}</span>
          <span className={styles.lineResult}>
            <span className={flag ? styles.flagged : undefined}>
              {lab.value ? [lab.value, lab.unit].filter(Boolean).join(' ') : 'On file'}
              {flag && ` \u00b7 ${flag.word}`}
            </span>
            {lab.where && <span className={styles.quiet}>{` \u00b7 ${lab.where}`}</span>}
            {lab.history.length > 0 && (
              <span className={styles.quiet}>
                {` \u00b7 earlier ${lab.history
                  .map((entry) => `${entry.value} (${formatVisitDate(entry.date, todayKey)})`)
                  .join(', ')}`}
              </span>
            )}
          </span>
        </li>
      );
    })}
  </ul>
);

OtherLabs.propTypes = {
  labs: PropTypes.arrayOf(PropTypes.object).isRequired,
  todayKey: PropTypes.string.isRequired,
};

const RecordLines = ({ lines, chartUrl, todayKey }) => (
  <ul className={styles.lines}>
    {lines.map((line) => {
      const flag = line.flag ? FLAG[line.flag] : null;
      return (
        <li key={line.key} className={styles.line}>
          <span className={styles.lineLabel}>{line.label}</span>
          <span className={styles.lineDate}>{formatVisitDate(line.date, todayKey)}</span>
          <span className={styles.lineResult}>
            <span className={flag ? styles.flagged : undefined}>
              {line.result}
              {flag && ` · ${flag.word}`}
            </span>
            {line.where && <span className={styles.quiet}>{` · ${line.where}`}</span>}
          </span>
          <span className={styles.lineLink}>
            <AthenaLink href={chartUrl} />
          </span>
        </li>
      );
    })}
  </ul>
);

RecordLines.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.object).isRequired,
  chartUrl: PropTypes.string,
  todayKey: PropTypes.string.isRequired,
};

const OrderedLines = ({ ordered, chartUrl, todayKey }) => {
  const lines = [
    ...ordered.lines.map((line) => ({ line, earlier: false })),
    ...ordered.earlier.map((line) => ({ line, earlier: true })),
  ];
  const onFile = ordered.lines.filter((line) => line.status === 'on-file').length;
  const orderedBy = ordered.orderedBy ? ` by ${ordered.orderedBy}` : '';
  const heading = `Ordered ${formatVisitDate(ordered.orderedOn, todayKey)}${orderedBy}`;

  return (
    <>
      {ordered.lines.length > 0 && (
        <p className={styles.orderedHead}>
          {heading}
          <span className={styles.orderedCount}>
            {` · ${onFile} of ${ordered.lines.length} on file`}
          </span>
        </p>
      )}
      <ul className={styles.lines}>
        {lines.map(({ line, earlier }) => {
          const onFileLine = line.status === 'on-file';
          const flag = line.flag ? FLAG[line.flag] : null;
          return (
            <li key={line.key} className={styles.line}>
              <span className={styles.lineLabel}>
                {line.label}
                {earlier && (
                  <span className={styles.lineNote}>
                    {`ordered ${formatVisitDate(line.orderedOn, todayKey)}`}
                  </span>
                )}
              </span>
              <span className={styles.lineDate}>
                {onFileLine && line.resultDate ? formatVisitDate(line.resultDate, todayKey) : ''}
              </span>
              <span className={styles.lineResult}>
                {onFileLine ? (
                  <>
                    <span className={flag ? styles.flagged : undefined}>
                      {line.result || 'On file'}
                      {flag && ` · ${flag.word}`}
                    </span>
                    {line.where && <span className={styles.quiet}>{` · ${line.where}`}</span>}
                  </>
                ) : (
                  <span className={styles.pending}>Not on file yet</span>
                )}
              </span>
              <span className={styles.lineLink}>
                {onFileLine && <AthenaLink href={chartUrl} />}
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
};

OrderedLines.propTypes = {
  ordered: PropTypes.shape({
    orderedOn: PropTypes.string,
    orderedBy: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object).isRequired,
    earlier: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  chartUrl: PropTypes.string,
  todayKey: PropTypes.string.isRequired,
};

const Notes = ({
  older = [],
  olderLabel = 'Older than the lookback, not shown',
  notFound = [],
  notFoundLead = 'Not found',
  searched = null,
}) => (
  <>
    {older.length > 0 && <p className={styles.note}>{`${olderLabel}: ${older.join(' · ')}`}</p>}
    {notFound.length > 0 && (
      <p className={styles.note}>{`${notFoundLead}: ${notFound.join(' · ')}`}</p>
    )}
    {searched && <p className={styles.note}>{searched}</p>}
  </>
);

Notes.propTypes = {
  older: PropTypes.arrayOf(PropTypes.string),
  olderLabel: PropTypes.string,
  notFound: PropTypes.arrayOf(PropTypes.string),
  notFoundLead: PropTypes.string,
  searched: PropTypes.string,
};

const ChartPrepSections = ({ records, chartUrl = null, todayKey }) => {
  if (!records) return null;
  const { labs, imaging, other, ordered, searched } = records;
  const notFoundLead = searched?.includes('Zus')
    ? 'Not found'
    : `Not found in ${searched ?? 'Athena'}`;

  const rows = [
    ordered && {
      label: 'Ordered last visit',
      group: ordered,
      body: <OrderedLines ordered={ordered} chartUrl={chartUrl} todayKey={todayKey} />,
    },
    showsLabs(labs) && {
      label: 'Labs',
      group: labs,
      olderLabel: 'Older than a year, not shown',
      body: (
        <>
          {labs.columns.length > 0 && (
            <LabsFlowsheet labs={labs} chartUrl={chartUrl} todayKey={todayKey} />
          )}
          <Disclosure count={labs.other.length} noun="recent lab">
            <OtherLabs labs={labs.other} todayKey={todayKey} />
          </Disclosure>
        </>
      ),
    },
    showsGroup(imaging) && {
      label: 'Imaging',
      group: imaging,
      olderLabel: 'Older than two years, not shown',
      body: (
        <>
          {imaging.lines.length > 0 && (
            <RecordLines lines={imaging.lines} chartUrl={chartUrl} todayKey={todayKey} />
          )}
          <Disclosure count={imaging.others.length} noun="imaging report">
            <RecordLines lines={imaging.others} chartUrl={chartUrl} todayKey={todayKey} />
          </Disclosure>
        </>
      ),
    },
    showsGroup(other) && {
      label: 'Other records',
      group: other,
      body: (
        <>
          {other.lines.length > 0 && (
            <RecordLines lines={other.lines} chartUrl={chartUrl} todayKey={todayKey} />
          )}
          <Disclosure count={other.others.length} noun="record">
            <RecordLines lines={other.others} chartUrl={chartUrl} todayKey={todayKey} />
          </Disclosure>
        </>
      ),
    },
  ].filter(Boolean);

  if (!rows.length) return null;

  return rows.map((row, index) => (
    <BriefRow key={row.label} label={row.label}>
      {row.body}
      <Notes
        older={row.group.older}
        olderLabel={row.olderLabel}
        notFound={row.group.notFound}
        notFoundLead={notFoundLead}
        searched={index === 0 ? getRecordsSearchedLine(records) : null}
      />
    </BriefRow>
  ));
};

ChartPrepSections.propTypes = {
  records: PropTypes.shape({
    asOf: PropTypes.string,
    searched: PropTypes.string,
    labs: PropTypes.object,
    imaging: PropTypes.object,
    other: PropTypes.object,
    ordered: PropTypes.object,
  }),
  chartUrl: PropTypes.string,
  todayKey: PropTypes.string.isRequired,
};

export default ChartPrepSections;
